import { api, API_BASE } from "@/lib/api";

/**
 * Upload a single File object to the backend. Returns the created FileRecord.
 * Extra context (project_id, folder_key, …) is passed as query params.
 */
export async function uploadFile(file, context = {}, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams();
  Object.entries(context).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") params.append(k, v);
  });
  const qs = params.toString() ? `?${params.toString()}` : "";
  const { data } = await api.post(`/files${qs}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return data;
}

export async function listFiles(context = {}) {
  const { data } = await api.get("/files", { params: context });
  return data;
}

export async function deleteFile(fileId) {
  const { data } = await api.delete(`/files/${fileId}`);
  return data;
}

const EXT_MIME = {
  pdf: "application/pdf",
  png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", svg: "image/svg+xml",
  mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm",
  mp3: "audio/mpeg", wav: "audio/wav", m4a: "audio/mp4",
  txt: "text/plain", csv: "text/csv", json: "application/json", md: "text/plain",
};

/** Best-guess MIME for a file record — stored types are sometimes octet-stream. */
export function resolveMime(file = {}) {
  const stored = file.content_type || "";
  if (stored && stored !== "application/octet-stream" && stored !== "binary/octet-stream") return stored;
  const ext = (file.original_filename || "").split(".").pop()?.toLowerCase();
  return EXT_MIME[ext] || stored || "application/octet-stream";
}

/**
 * Returns a Blob URL (URL.createObjectURL) for a stored file. The caller is
 * responsible for calling URL.revokeObjectURL(url) when done. Uses the
 * download endpoint with the browser's auth cookie so <img> tags without
 * header-support still work. `mimeHint` forces the blob type so browsers
 * render PDFs inline instead of showing a download strip.
 */
export async function fileBlobUrl(fileId, mimeHint) {
  const resp = await fetch(`${API_BASE}/files/${fileId}/download`, {
    credentials: "include",
  });
  if (!resp.ok) throw new Error(`download failed: ${resp.status}`);
  const blob = await resp.blob();
  const typed = mimeHint && mimeHint !== blob.type ? new Blob([blob], { type: mimeHint }) : blob;
  return URL.createObjectURL(typed);
}

export function humanBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function iconForMime(mime = "") {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "film";
  if (mime.startsWith("audio/")) return "music";
  if (mime === "application/pdf") return "file-text";
  if (mime.includes("word")) return "file-text";
  if (mime.includes("sheet") || mime === "text/csv") return "table";
  if (mime.includes("presentation")) return "presentation";
  if (mime.includes("zip") || mime.includes("compressed")) return "archive";
  return "file";
}
